import { defineStore } from 'pinia'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import api from '@dashboard/services/axiosApi'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { useConfigStore } from '@dashboard/stores/configStore'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

// Add type declaration for Echo to fix linter errors
declare const Echo: {
  leave: (channel: string) => void
  private: (channel: string) => {
    listen: (event: string, callback: () => void) => void
  }
}

export const usePersonalizedEmailStore = defineStore('personalizedEmail', () => {
  const configStore = useConfigStore()
  const { currentTeamPid } = storeToRefs(configStore)
  const queryClient = useQueryClient()

  const isGenerating = ref(false)

  watch(
    currentTeamPid,
    (newVal, oldVal) => {
      if (oldVal) {
        Echo.leave(`personalized-emails.${oldVal}`)
      }

      if (newVal && newVal !== 'demo') {
        Echo.private(`personalized-emails.${newVal}`).listen('PersonalizedEmailsUpdated', () => {
          console.log('fetching')
          void emailsQuery.refetch()
        })
      }
    },
    { immediate: true }
  )

  const emailsQuery = useQuery({
    queryKey: ['personalizedEmails', currentTeamPid],
    enabled: computed(() => Boolean(currentTeamPid.value)),
    queryFn: () => api.get(`/api/${currentTeamPid.value}/personalizedEmails`).then((res) => res.data)
  })

  const emails = computed(() => emailsQuery.data.value)

  // Add proper type for email
  interface Email {
    pid: string
    tags: string[]
    [key: string]: any
  }

  const getById = (id: string) => (emails.value || []).find((email: Email) => String(email.pid) === id)

  const availableTags = computed(() => {
    const tags: string[] = []
    emails.value?.forEach((email: Email) => {
      email.tags?.forEach((tag: string) => {
        if (!tags.includes(tag)) {
          tags.push(tag)
        }
      })
    })
    return tags
  })

  const destroy = async (pid: string) => {
    const email = getById(pid)

    await api.delete(`/api/${currentTeamPid.value}/personalizedEmails/${pid}`)

    // Track email deletion
    void recordAnalyticsEvent(AnalyticsEventTypes.EMAIL_DELETED, {
      emailId: pid,
      emailName: email?.name
    })

    await emailsQuery.refetch()
  }

  const create = async (email: { name?: string; original_email?: string } = {}) => {
    const emailName = email.name || `Email #${(emails.value || []).length + 1}`

    const response = await api.post(`/api/${currentTeamPid.value}/personalizedEmails`, {
      name: emailName,
      original_email: email.original_email || ''
    })

    const newEmail = response.data

    // Track email creation
    void recordAnalyticsEvent(AnalyticsEventTypes.EMAIL_CREATED, {
      emailId: newEmail.pid,
      emailName: newEmail.name
    })

    await emailsQuery.refetch()

    return newEmail
  }

  const updateEmailMutation = useMutation({
    mutationFn: ({ pid, data }: { pid: string; data: Partial<Email> }) =>
      api.put(`/api/${currentTeamPid.value}/personalizedEmails/${pid}`, data),
    onSuccess: (response, variables) => {
      // We still update the store data, but this won't affect the component's local state
      const updatedEmail = response?.data
      if (updatedEmail) {
        const index = (emails.value || []).findIndex((email: Email) => String(email.pid) === variables.pid)
        if (index !== -1) {
          const updatedEmails = [...emails.value]
          updatedEmails[index] = updatedEmail
          emailsQuery.data.value = updatedEmails
        }
      }
      // Also invalidate the query to ensure fresh data
      void queryClient.invalidateQueries({
        queryKey: ['personalizedEmails', currentTeamPid.value]
      })
    }
  })

  const update = (pid: string, data: Partial<Email>) => updateEmailMutation.mutateAsync({ pid, data })

  const chatMutation = useMutation({
    mutationFn: ({ pid, message }: { pid: string; message: string }) => {
      isGenerating.value = true
      return api
        .post(`/api/${currentTeamPid.value}/personalizedEmails/${pid}/chat`, {
          message
        })
        .then((res) => res.data)
    },
    onSuccess: (data) => {
      void emailsQuery.refetch()
      isGenerating.value = false
    }
  })

  return {
    emails,
    availableTags,
    getById,
    create,
    destroy,
    update,
    chatMutation,
    emailsQuery,
    isGenerating
  }
})
